import React from 'react'
import PropTypes from 'prop-types'
import MuiSelect from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import OutlinedInput from '@mui/material/OutlinedInput'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import { tagChipSx } from '../chipStyles'

// Matches the app's actual placeholder color (confirmed via computed-style
// probe against a live text input's ::placeholder, not assumed) — the same
// var(--vo-steel) used across the codebase's own form field placeholders, distinct
// from CLOSED_SX's full-strength var(--vo-ink) text color.
const PLACEHOLDER_COLOR = 'var(--vo-steel)'

// True exactly when the single-select value is the empty-string sentinel
// every migrated site uses for "no selection" — the same condition under
// which displayEmpty renders the options array's placeholder entry.
const isEmptyValue = (v) => v === '' || v === null || v === undefined

// MuiSelect's own selection logic (highlighting the checked MenuItem,
// toggling multiple's value array on click) runs on plain indexOf/===
// against whatever `value` it's handed, with no prop to override it —
// unlike Autocomplete, Select has no isOptionEqualToValue hook of its own.
// That silently breaks for any consumer whose value flows through
// react-hook-form's Controller, since RHF clones stored field values on
// every render: the object handed back is never the same reference as the
// matching entry still sitting in `options`, even though it's the same
// record. Mirrors Autocomplete's defaultIsOptionEqualToValue — reference
// equality is checked first, so this is never less correct than plain
// ===; it only adds a fallback for the common _id/id-keyed-record shape
// used throughout this codebase. A consumer can still override this via
// the isOptionEqualToValue prop for anything more specific.
const defaultIsOptionEqualToValue = (option, value) => {
  if (option === value) return true
  if (option && value && typeof option === 'object' && typeof value === 'object') {
    if (option._id !== undefined && value._id !== undefined) return option._id === value._id
    if (option.id !== undefined && value.id !== undefined) return option.id === value.id
  }
  return false
}

// Primitives pass through unchanged as React keys. Object-shaped values
// (Managers' full company objects, e.g.) have no stable primitive to key
// on, so every MenuItem/Chip would otherwise collide on the same
// "[object Object]" key — resolve to _id/id, falling back to the option's
// own label as a last resort.
const getOptionKey = (value, fallbackLabel) => {
  if (value && typeof value === 'object') {
    return value._id ?? value.id ?? fallbackLabel
  }
  return value
}

// Border-color rules shared by both the single-select and multiple-mode
// closed boxes below — the only piece that ever needed to vary per
// consumer (an error state), which used to force every error-capable
// multi-select site to hand-roll its entire box just to get a red border.
// Native here now, so no consumer override is needed for that alone.
const closedBorderSx = (error) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--vo-line)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--vo-line)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--color-brand)',
    borderWidth: '1px',
  },
})

const CLOSED_SX_SELECT_KEY = '& .MuiSelect-select.MuiSelect-select'

// Closed-state box only — matches the legacy FilterSelect look (height 40,
// border var(--vo-line), focus var(--color-brand), 'Lato' 13px) so every migrated site keeps
// its prior visual weight. Never touches the open menu/popover.
const CLOSED_SX = (error) => ({
  // .MuiSelect-select doubled: MUI's own generated class for this slot
  // (padding/height/line-height baked in via MuiInputBase-input +
  // MuiOutlinedInput-input + MuiSelect-select all on one node) otherwise
  // wins the specificity tie against a single-class sx selector.
  [CLOSED_SX_SELECT_KEY]: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    height: '40px',
    padding: '0 32px 0 12px',
    fontFamily: "'Lato', sans-serif",
    fontSize: '13px',
    color: 'var(--vo-ink)',
  },
  ...closedBorderSx(error),
})

// Same 40px baseline as CLOSED_SX, but `minHeight` in place of a fixed
// `height` so the box can grow when chips wrap to a second row, and a
// small vertical padding (4px top/bottom) tuned against tagChipSx's actual
// rendered height (MUI's default "medium" Chip, 32px) so one row of chips
// sits flush at 40px total — matching the single-line baseline exactly
// instead of drifting to an ad hoc number, as every prior multi-value
// consumer of this component had to.
const MULTIPLE_CLOSED_SX = (error) => ({
  [CLOSED_SX_SELECT_KEY]: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    boxSizing: 'border-box',
    minHeight: '40px',
    padding: '4px 32px 4px 12px',
    fontFamily: "'Lato', sans-serif",
    fontSize: '13px',
    color: 'var(--vo-ink)',
  },
  ...closedBorderSx(error),
})

// A consumer that needs to touch the same slot CLOSED_SX already claims
// (Scanner dashboard's RiskRadar/ThreatMap shrink the compact height
// further and use their own text color) can't just add a plain
// '& .MuiSelect-select' key to their own sx object and expect it to win:
// confirmed via a live-browser computed-style probe (jsdom's
// getComputedStyle doesn't apply emotion's injected stylesheet, so this
// isn't checkable in a unit test) that CLOSED_SX's doubled selector beats a
// single-class consumer selector on specificity regardless of array order,
// silently reverting color/padding/height/font-size. Folding any
// '& .MuiSelect-select' override the consumer supplies into CLOSED_SX's own
// doubled-selector object — so both land in the same emitted rule, where it
// really is plain last-property-wins — fixes this without touching
// CLOSED_SX's behavior for every scalar single-select site that never hits
// this collision.
const mergeClosedSx = (sx, error) => {
  const base = CLOSED_SX(error)
  const overrides = Array.isArray(sx) ? sx : [sx]
  const mergedSelectSx = { ...base[CLOSED_SX_SELECT_KEY] }
  const rest = overrides.map((o) => {
    if (!o) return o
    const { '& .MuiSelect-select': selectOverride, ...others } = o
    if (selectOverride) Object.assign(mergedSelectSx, selectOverride)
    return others
  })
  return [{ ...base, [CLOSED_SX_SELECT_KEY]: mergedSelectSx }, ...rest]
}

// Same folding trick as mergeClosedSx, for the `multiple` closed box —
// lets a residual consumer sx (e.g. Managers' marginBottom) still land
// without fighting MULTIPLE_CLOSED_SX's own doubled-selector specificity.
const mergeMultipleClosedSx = (sx, error) => {
  const base = MULTIPLE_CLOSED_SX(error)
  const overrides = Array.isArray(sx) ? sx : [sx]
  const mergedSelectSx = { ...base[CLOSED_SX_SELECT_KEY] }
  const rest = overrides.map((o) => {
    if (!o) return o
    const { '& .MuiSelect-select': selectOverride, ...others } = o
    if (selectOverride) Object.assign(mergedSelectSx, selectOverride)
    return others
  })
  return [{ ...base, [CLOSED_SX_SELECT_KEY]: mergedSelectSx }, ...rest]
}

export const Select = React.forwardRef(function Select({
  label, options, value, onChange, multiple,
  error, helperText, fullWidth, disabled, name, sx, isOptionEqualToValue, onOpen,
  ...rest
}, ref) {
  const labelId = `${name}-label`
  const resolveOption = (v) => options.find((opt) => isOptionEqualToValue(opt.value, v))
  const getLabel = (v) => resolveOption(v)?.label ?? v?.name ?? v?.label ?? ''

  // Normalizes `value` to the literal references living in `options`
  // before handing it to MuiSelect, mirroring what utils/select_utils.js's
  // getSelectedContent does today by hand for Managers — this is the only
  // place the reference-equality problem above can actually be fixed,
  // since MuiSelect's internal indexOf/=== has no override point. Falls
  // back to the original entry when nothing matches (e.g. before options
  // has loaded) so a selection is never silently dropped.
  const resolveValue = (v) => resolveOption(v)?.value ?? v
  const resolvedValue = multiple
    ? (value || []).map(resolveValue)
    : resolveValue(value ?? '')

  return (
    <FormControl fullWidth={fullWidth} error={!!error} disabled={disabled}>
      {/* shrink unconditional, not mode-dependent: displayEmpty is always
          set below (both modes), so the box always shows visible content
          (a real value or the placeholder option's label) and the label
          must always sit in the floated/notched position to match — this
          is MUI's own documented pairing for displayEmpty. Auto-detection
          (the default when shrink is omitted) only shrinks on focus or a
          non-empty value, so an empty-valued, disabled — and therefore
          never-focusable — single-select would leave the label sitting
          inline over the placeholder text instead: confirmed via a live
          before/after comparison against the Country field, the first
          consumer to pair `label` with an empty initial value. */}
      {label && <InputLabel id={labelId} shrink>{label}</InputLabel>}
      <MuiSelect
        ref={ref}
        labelId={labelId}
        name={name}
        multiple={multiple}
        displayEmpty
        value={resolvedValue}
        onChange={(e) => onChange(e.target.value)}
        onOpen={onOpen}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        sx={multiple ? mergeMultipleClosedSx(sx, error) : mergeClosedSx(sx, error)}
        renderValue={multiple ? (selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((v) => (
              <Chip
                key={getOptionKey(v, getLabel(v))}
                label={getLabel(v)}
                sx={tagChipSx}
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={(e) => {
                  e.stopPropagation()
                  onChange(value.filter((x) => !isOptionEqualToValue(x, v)))
                }}
              />
            ))}
          </Box>
        ) : (selected) => (
          <span style={isEmptyValue(selected) ? { color: PLACEHOLDER_COLOR } : undefined}>
            {resolveOption(selected)?.label ?? ''}
          </span>
        )}
        SelectDisplayProps={rest}
      >
        {options.map((opt) => (
          <MenuItem key={getOptionKey(opt.value, opt.label)} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
})

Select.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  value: PropTypes.any,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isOptionEqualToValue: PropTypes.func,
  onOpen: PropTypes.func,
}

// fullWidth defaults true: the legacy native <select> always carried its own
// explicit width: 100%, independent of whatever layout container it sat in.
// FormControl's own inline-flex default has no such guarantee — it only
// stretches when an ancestor forces it (a flex column's default
// align-items: stretch, e.g.), so plain-<div>-wrapped sites silently
// shrink to content width instead of filling their field. No call site
// currently opts out; a narrower single instance still overrides via sx.
Select.defaultProps = {
  options: [],
  multiple: false,
  error: false,
  sx: {},
  fullWidth: true,
  isOptionEqualToValue: defaultIsOptionEqualToValue,
}
